import { z } from "zod";
import { oc } from "@orpc/contract";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Plaid Bank Account Schema
 */
export const PlaidBankAccountSchema = z.object({
  id: z.string(), // unique ID for this connection
  accessToken: z.string(), // Plaid access token (will be obfuscated on frontend)
  itemId: z.string(), // Plaid item ID
  institutionName: z.string(), // Bank name
  institutionId: z.string(), // Plaid institution ID
  accountIds: z.array(z.string()), // Array of account IDs from this connection
  connectedAt: z.coerce.date(),
  checkbookBankId: z.string().optional(), // Cache Checkbook bank ID after first registration
});

/**
 * Plaid Integration Settings Schema
 */
export const PlaidIntegrationSettingsSchema = z.object({
  accounts: z.array(PlaidBankAccountSchema).prefault([]),
});

/**
 * Plaid Integration Settings Type
 */
export type PlaidIntegrationSettings = z.infer<
  typeof PlaidIntegrationSettingsSchema
>;

/**
 * Plaid Bank Account Type
 */
export type PlaidBankAccount = z.infer<typeof PlaidBankAccountSchema>;

/**
 * Plaid integration variant (without base fields)
 */
export const PlaidIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.Plaid),
  settings: PlaidIntegrationSettingsSchema,
});

/**
 * Full Plaid integration schema (with base fields)
 */
export const PlaidIntegrationSchema = IntegrationBaseSchema.merge(
  PlaidIntegrationSettingsSchemaWithoutBase,
);

/**
 * Plaid Integration Type
 */
export type PlaidIntegration = z.infer<typeof PlaidIntegrationSchema>;

/**
 * Create Plaid Link Token Contract
 */
const createPlaidLinkToken = oc
  .output(z.object({ linkToken: z.string(), expiration: z.coerce.date() }))
  .route({ method: "POST" });

/**
 * Exchange Plaid Public Token Contract
 * Note: Returns PlaidIntegrationSchema directly to avoid circular dependency
 */
const exchangePlaidPublicToken = oc
  .input(
    z.object({
      publicToken: z.string(),
      institutionName: z.string(),
      institutionId: z.string(),
      accountIds: z.array(z.string()),
    }),
  )
  .output(PlaidIntegrationSchema)
  .route({ method: "POST" });

/**
 * Delete Plaid Bank Account Contract
 * Note: Returns PlaidIntegrationSchema directly to avoid circular dependency
 */
const deletePlaidBankAccount = oc
  .input(z.object({ accountId: z.string() }))
  .output(PlaidIntegrationSchema)
  .route({ method: "DELETE" });

/**
 * Create Plaid Update Mode Link Token Contract
 * Used for re-authenticating an existing bank connection
 */
const createPlaidUpdateLinkToken = oc
  .input(z.object({ accountId: z.string() }))
  .output(z.object({ linkToken: z.string(), expiration: z.coerce.date() }))
  .route({ method: "POST" });

/**
 * Plaid Contracts
 */
export const plaidContracts = {
  createLinkToken: createPlaidLinkToken,
  exchangePublicToken: exchangePlaidPublicToken,
  deleteBankAccount: deletePlaidBankAccount,
  createUpdateLinkToken: createPlaidUpdateLinkToken,
} as const;

/**
 * Plaid Contracts Type
 */
export type PlaidContracts = typeof plaidContracts;
